import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import AuthModal from "@/components/AuthModal";
import SuccessOverlay from "@/components/SuccessOverlay";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Rocket,
  Layers,
  Film,
  Image as ImageIcon,
  GalleryHorizontal,
  Compass,
  Plus,
  X,
  Lock,
  Info,
  Clock,
  CreditCard,
  Clapperboard,
  Scissors,
} from "lucide-react";

/* ---------------- static UI meta ---------------- */

const SERVICE_ICON_MAP = {
  premium_short: Clapperboard,
  volume_short: Scissors,
  long_form: Film,
  thumbnails: ImageIcon,
  carousels: GalleryHorizontal,
  strategy: Compass,
};

// Per-icon size overrides — some Lucide glyphs are visually lighter than
// others (Image/GalleryHorizontal cover less of their viewBox), so we
// nudge them up a couple pixels for consistent optical weight.
const SERVICE_ICON_SIZE = {
  thumbnails: 19,
  carousels: 19,
  strategy: 18,
};

const DRAFT_KEY = "bliencer_order_draft_v2";
const CADENCE_OPTIONS = [
  { id: "one_time", label: "One-time" },
  { id: "monthly", label: "Monthly · −5%" },
  { id: "yearly", label: "Annual · −25%" },
];
const DISCOUNTS = { one_time: 0, monthly: 0.05, quarterly: 0.12, yearly: 0.25 };

/* ---------------- helpers ---------------- */

function newServiceEntry() {
  return {
    style_id: "",
    custom_style: "",
    quantity: 0,
    footage_minutes: 0,
    source_links: [""],
    source_context: "",
    notes: "",
  };
}

function serviceUnitPrice(svc, entry) {
  if (!svc) return 0;
  let line = (svc.base_price || 0) * (entry.quantity || 0);
  if (svc.ask_footage_length && svc.per_minute_price) {
    line += (svc.per_minute_price || 0) * (entry.footage_minutes || 0);
  }
  return line;
}

/* ================================================================== */

export default function Order() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();

  const [pkg, setPkg] = useState(null);
  const [services, setServices] = useState([]);
  const [step, setStep] = useState(1); // 1..4
  const [subStep, setSubStep] = useState("style"); // when step=2: "style" or "qty"
  const [selected, setSelected] = useState([]); // list of service_id
  const [data, setData] = useState({}); // { [service_id]: entry }
  const [activeIdx, setActiveIdx] = useState(0);
  const [subscription, setSubscription] = useState("one_time");
  const [authOpen, setAuthOpen] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [successFor, setSuccessFor] = useState(null); // order_id when success overlay is visible
  const [primeMe, setPrimeMe] = useState(null);

  /* -------------- load catalog -------------- */
  useEffect(() => {
    api.get("/packages").then((r) => {
      setPkg(r.data);
      setServices(r.data.services || []);
    });
  }, []);

  useEffect(() => {
    if (!user) { setPrimeMe(null); return; }
    api.get("/prime/me").then((r) => setPrimeMe(r.data)).catch(() => {});
  }, [user]);

  /* -------------- restore / persist draft -------------- */
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const d = JSON.parse(raw);
        if (d.selected?.length) {
          setSelected(d.selected);
          setData(d.data || {});
          setSubscription(d.subscription || "one_time");
          setStep(d.step || 1);
        }
      }
    } catch {}
  }, []);

  useEffect(() => {
    if (selected.length) {
      localStorage.setItem(
        DRAFT_KEY,
        JSON.stringify({ selected, data, subscription, step }),
      );
    }
  }, [selected, data, subscription, step]);

  /* -------------- computed -------------- */
  const activeSvcId = selected[activeIdx];
  const activeSvc = useMemo(
    () => services.find((s) => s.id === activeSvcId),
    [services, activeSvcId],
  );

  const priceBreakdown = useMemo(() => {
    const rows = selected.map((sid) => {
      const svc = services.find((s) => s.id === sid);
      const entry = data[sid] || newServiceEntry();
      return {
        sid,
        name: svc?.name || sid,
        unit: svc?.unit_label,
        qty: entry.quantity,
        line: serviceUnitPrice(svc, entry),
      };
    });
    const subtotal = rows.reduce((s, r) => s + r.line, 0);
    // Bliencer Prime discount takes precedence over the legacy per-order
    // subscription selector (which is now deprecated).
    const primeActive = primeMe?.status === "active";
    const primeRate = primeActive ? (primeMe?.discount_percentage || 0) / 100 : 0;
    const discountRate = primeActive ? primeRate : (DISCOUNTS[subscription] || 0);
    const discount = subtotal * discountRate;
    const total = subtotal - discount;
    return { rows, subtotal, discount, discountRate, total, primeActive, primePlan: primeMe?.plan };
  }, [selected, data, services, subscription, primeMe]);

  /* -------------- selection helpers -------------- */
  const toggleService = (sid) => {
    setSelected((prev) => {
      const next = prev.includes(sid) ? prev.filter((x) => x !== sid) : [...prev, sid];
      setData((d) => {
        const nd = { ...d };
        if (!prev.includes(sid) && !nd[sid]) nd[sid] = newServiceEntry();
        return nd;
      });
      return next;
    });
  };

  const patchEntry = (sid, patch) =>
    setData((d) => ({ ...d, [sid]: { ...(d[sid] || newServiceEntry()), ...patch } }));

  /* -------------- validation gates -------------- */
  const canGoNext = () => {
    if (step === 1) return selected.length > 0;
    if (step === 2) {
      const svc = activeSvc;
      const e = data[activeSvc?.id] || {};
      if (!svc) return false;
      if (subStep === "style") return !!e.style_id;
      // quantity substep
      if (!e.quantity || e.quantity < 1) return false;
      if (svc.ask_footage_length && (!e.footage_minutes || e.footage_minutes < 1)) return false;
      return true;
    }
    if (step === 3) {
      const svc = activeSvc;
      const e = data[activeSvc?.id] || {};
      if (!svc) return false;
      if (!svc.requires_raw_content) return true;
      const hasLink = (e.source_links || []).some((l) => l.trim());
      const hasText = svc.allow_context_text && (e.source_context || "").trim().length > 0;
      return hasLink || hasText;
    }
    return true;
  };

  const goNext = () => {
    if (step === 1) {
      setActiveIdx(0);
      // If the first selected service skips styles, jump straight to quantity
      const firstSvc = services.find((s) => s.id === selected[0]);
      if (firstSvc?.skip_style_selection) {
        // pre-fill style_id so downstream code stays happy
        setData((d) => ({
          ...d,
          [firstSvc.id]: { ...(d[firstSvc.id] || {}), style_id: d[firstSvc.id]?.style_id || "editor_choice" },
        }));
        setSubStep("qty");
      } else {
        setSubStep("style");
      }
      setStep(2);
    } else if (step === 2) {
      if (subStep === "style") {
        setSubStep("qty");
      } else if (activeIdx < selected.length - 1) {
        const nextIdx = activeIdx + 1;
        const nextSvc = services.find((s) => s.id === selected[nextIdx]);
        setActiveIdx(nextIdx);
        if (nextSvc?.skip_style_selection) {
          setData((d) => ({
            ...d,
            [nextSvc.id]: { ...(d[nextSvc.id] || {}), style_id: d[nextSvc.id]?.style_id || "editor_choice" },
          }));
          setSubStep("qty");
        } else {
          setSubStep("style");
        }
      } else {
        setActiveIdx(0);
        setStep(3);
      }
    } else if (step === 3) {
      if (activeIdx < selected.length - 1) {
        setActiveIdx((i) => i + 1);
      } else {
        setStep(4);
      }
    } else if (step === 4) {
      submit();
    }
  };

  const goBack = () => {
    if (step === 1) return navigate(-1);
    if (step === 2) {
      if (subStep === "qty" && !activeSvc?.skip_style_selection) {
        setSubStep("style");
      } else if (activeIdx > 0) {
        const prevIdx = activeIdx - 1;
        const prevSvc = services.find((s) => s.id === selected[prevIdx]);
        setActiveIdx(prevIdx);
        setSubStep("qty");
      } else {
        setStep(1);
      }
    } else if (step === 3) {
      if (activeIdx > 0) setActiveIdx((i) => i - 1);
      else {
        setActiveIdx(selected.length - 1);
        setSubStep("qty");
        setStep(2);
      }
    } else if (step === 4) {
      setActiveIdx(selected.length - 1);
      setStep(3);
    }
  };

  /* -------------- submit -------------- */
  const submitOrder = useCallback(async () => {
    // Defense-in-depth: never POST an order without an authenticated user.
    if (!user) {
      setAuthOpen(true);
      return;
    }
    setPlacing(true);
    try {
      const payloadServices = selected.map((sid) => {
        const svc = services.find((s) => s.id === sid);
        const e = data[sid] || {};
        const styleId = svc?.skip_style_selection
          ? (e.style_id || "editor_choice")
          : (e.style_id || "custom");
        return {
          service_id: sid,
          style_id: styleId,
          custom_style: styleId === "custom" ? e.custom_style : null,
          quantity: parseInt(e.quantity || 1, 10),
          footage_minutes: svc?.ask_footage_length ? parseInt(e.footage_minutes || 0, 10) : null,
          source_links: (e.source_links || []).filter((l) => l.trim()),
          source_context: e.source_context || null,
          notes: e.notes || null,
        };
      });
      const { data: order } = await api.post("/orders", {
        services: payloadServices,
        subscription,
      });
      localStorage.removeItem(DRAFT_KEY);
      toast.success("Order placed. Editors will be assigned shortly.");
      setSuccessFor(order.order_id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to place order");
    } finally {
      setPlacing(false);
    }
  }, [selected, data, services, subscription, user]);

  const submit = () => {
    // Defensive gate: never submit while we're still resolving auth or when
    // the user is not signed in. Backend also rejects unauthenticated posts,
    // but stopping here gives the client the sign-in modal instead of a 401.
    if (authLoading) return;
    if (!user) {
      setAuthOpen(true);
      return;
    }
    submitOrder();
  };

  useEffect(() => {
    if (searchParams.get("resume") === "1" && user && selected.length) {
      searchParams.delete("resume");
      setSearchParams(searchParams, { replace: true });
      // Stay on the review step — user must click "Complete payment" themselves
      setStep(4);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, services]);

  /* -------------- render -------------- */
  const progressPct = useMemo(() => {
    if (step === 1) return selected.length ? 20 : 5;
    if (step === 2) {
      const perSvc = 30 / Math.max(1, selected.length);
      const done = activeIdx + (subStep === "qty" ? 1 : 0.5);
      return 20 + perSvc * done;
    }
    if (step === 3) {
      const perSvc = 30 / Math.max(1, selected.length);
      return 50 + perSvc * (activeIdx + 1);
    }
    return 100;
  }, [step, subStep, activeIdx, selected.length]);

  return (
    <div className="min-h-screen bg-black text-white">
      <Nav />
      <div className="pt-28 pb-24 px-6 sm:px-10">
        <div className="max-w-4xl mx-auto">
          {/* header */}
          <div className="mb-8">
            <div className="text-[11px] uppercase tracking-widest text-[#a0a0a0]">
              {step === 1 && "Step 1 · What do you need?"}
              {step === 2 &&
                `Step 2 · ${activeSvc?.name || "Configure"} — ${
                  subStep === "style" ? "pick a style" : "pick a quantity"
                }`}
              {step === 3 && `Step 3 · Provide content · ${activeSvc?.name || ""}`}
              {step === 4 && "Step 4 · Review your order"}
            </div>
            <h1
              className="mt-2 text-4xl sm:text-5xl font-bold tracking-tighter"
              data-testid="order-title"
            >
              {step === 1 && "Build your order."}
              {step === 2 && subStep === "style" && "Pick a style."}
              {step === 2 && subStep === "qty" && "How much do you need?"}
              {step === 3 && "Share your content."}
              {step === 4 && "You're set."}
            </h1>
            {selected.length > 0 && step > 1 && (
              <div className="mt-4 flex flex-wrap gap-1.5" data-testid="service-chip-strip">
                {selected.map((sid, i) => {
                  const svc = services.find((s) => s.id === sid);
                  const active = i === activeIdx && step !== 4;
                  return (
                    <span
                      key={sid}
                      className={`text-[11px] uppercase tracking-widest px-2.5 py-1 rounded-full border ${
                        active
                          ? "bg-white text-black border-white"
                          : "bg-[#0d0d0d] text-[#a0a0a0] border-[#2b2b2b]"
                      }`}
                    >
                      {svc?.name || sid}
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          {/* Progress rail */}
          <div className="progress-track mb-3" data-testid="order-progress">
            <motion.div
              className="progress-fill"
              initial={false}
              animate={{ width: `${progressPct}%` }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            />
          </div>

          {/* Subtle estimated running total (steps 2 & 3 only, hidden on step 4) */}
          {step > 1 && step < 4 && priceBreakdown.subtotal > 0 && (
            <div
              className="mb-8 flex items-center justify-end text-[11px] uppercase tracking-widest text-[#7a7a7a]"
              data-testid="running-total-hint"
            >
              <span className="mr-2">Estimated so far</span>
              <span
                className="font-semibold text-white tabular-nums"
                style={{ color: "#8aa4ff" }}
              >
                ~${Math.round(priceBreakdown.subtotal)}
              </span>
            </div>
          )}
          {step > 1 && step < 4 && priceBreakdown.subtotal === 0 && (
            <div className="mb-8" />
          )}
          {step <= 1 && <div className="mb-7" />}

          <AnimatePresence mode="wait">
            <motion.div
              key={`${step}-${subStep}-${activeIdx}`}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            >
              {step === 1 && (
                <Step1Services
                  services={services}
                  selected={selected}
                  onToggle={toggleService}
                />
              )}
              {step === 2 && subStep === "style" && activeSvc && (
                <Step2Style
                  svc={activeSvc}
                  entry={data[activeSvc.id] || newServiceEntry()}
                  onChange={(patch) => patchEntry(activeSvc.id, patch)}
                />
              )}
              {step === 2 && subStep === "qty" && activeSvc && (
                <Step2Quantity
                  svc={activeSvc}
                  entry={data[activeSvc.id] || newServiceEntry()}
                  onChange={(patch) => patchEntry(activeSvc.id, patch)}
                />
              )}
              {step === 3 && activeSvc && (
                <Step3Content
                  svc={activeSvc}
                  entry={data[activeSvc.id] || newServiceEntry()}
                  onChange={(patch) => patchEntry(activeSvc.id, patch)}
                />
              )}
              {step === 4 && (
                <Step4Review
                  services={services}
                  selected={selected}
                  data={data}
                  subscription={subscription}
                  setSubscription={setSubscription}
                  price={priceBreakdown}
                  primeMe={primeMe}
                />
              )}
            </motion.div>
          </AnimatePresence>

          {/* Nav footer */}
          <div className="mt-12 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={goBack}
              data-testid="order-back-btn"
              className="squircle-sm border border-[#2b2b2b] px-5 h-11 text-sm inline-flex items-center gap-2 hover:bg-[#111] btn-anim"
            >
              <ArrowLeft size={14} /> Back
            </button>
            <button
              type="button"
              onClick={goNext}
              disabled={!canGoNext() || placing || (step === 4 && authLoading)}
              data-testid="order-next-btn"
              className={`btn-accent squircle-sm px-6 h-11 text-sm font-medium btn-anim inline-flex items-center gap-2 ${
                !canGoNext() || placing ? "opacity-40 cursor-not-allowed" : ""
              }`}
            >
              {step < 4 ? (
                <>
                  Continue <ArrowRight size={14} />
                </>
              ) : placing ? (
                <>Processing…</>
              ) : authLoading ? (
                <>Checking session…</>
              ) : user ? (
                <>
                  <CreditCard size={14} /> Complete payment · ${Math.round(priceBreakdown.total)}
                </>
              ) : (
                <>
                  <Lock size={14} /> Sign in to continue
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      <AuthModal
        open={authOpen}
        onClose={() => setAuthOpen(false)}
        redirectPath={`/order?resume=1`}
        onSuccess={() => {
          // Keep client on the review page — they'll click "Complete payment"
          // themselves so they see the final total one more time.
          setAuthOpen(false);
          setStep(4);
          toast.success("Signed in. Review your order and confirm payment.");
        }}
      />

      <AnimatePresence>
        {successFor && (
          <SuccessOverlay
            title="Order placed"
            subtitle="Taking you to your order…"
            onDone={() => navigate(`/dashboard/order/${successFor}`)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

/* ================================================================== */
/* Step 1: Service selection                                          */
/* ================================================================== */
function Step1Services({ services, selected, onToggle }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="step-1">
      {services.map((svc) => {
        const Icon = SERVICE_ICON_MAP[svc.id] || Rocket;
        const iconSize = SERVICE_ICON_SIZE[svc.id] || 16;
        const isSelected = selected.includes(svc.id);
        return (
          <button
            key={svc.id}
            type="button"
            onClick={() => onToggle(svc.id)}
            data-testid={`service-card-${svc.id}`}
            data-selected={isSelected}
            className={`text-left squircle border p-4 btn-anim relative overflow-hidden ${
              isSelected
                ? "border-[#6589ff] bg-[#0d0d0d]"
                : "border-[#2b2b2b] bg-[#0d0d0d] hover:border-[#3a3a3a] hover:bg-[#111]"
            }`}
            style={isSelected ? { boxShadow: "0 0 0 2px rgba(101,137,255,0.25)" } : undefined}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="h-9 w-9 squircle-sm bg-[#111] border border-[#2b2b2b] flex items-center justify-center">
                <Icon size={iconSize} strokeWidth={svc.id === "thumbnails" || svc.id === "carousels" ? 2.1 : 2} />
              </div>
              <div
                className={`h-5 w-5 rounded-full flex items-center justify-center border ${
                  isSelected ? "border-transparent" : "border-[#3a3a3a]"
                }`}
                style={isSelected ? { background: "#6589ff" } : undefined}
              >
                {isSelected && <Check size={11} style={{ color: "#fff" }} />}
              </div>
            </div>
            <div className="text-[15px] font-semibold tracking-tight leading-snug min-h-[3rem]">
              {svc.name}
            </div>
            <div className="text-xs text-[#a0a0a0] mt-1.5 leading-snug min-h-[2.2rem]">
              {svc.tagline}
            </div>
          </button>
        );
      })}
    </div>
  );
}

/* ================================================================== */
/* Step 2A: Style                                                     */
/* ================================================================== */
function Step2Style({ svc, entry, onChange }) {
  return (
    <div data-testid="step-2-style">
      <p className="text-[#a0a0a0] max-w-lg mb-6">
        Pick the aesthetic you want for your <span className="text-white">{svc.name}</span>.
        You can describe a custom direction below.
      </p>

      <div className="grid sm:grid-cols-2 gap-3">
        {svc.styles.map((s) => {
          const active = entry.style_id === s.id;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => onChange({ style_id: s.id })}
              data-testid={`style-card-${svc.id}-${s.id}`}
              className={`text-left squircle border p-5 btn-anim ${
                active
                  ? "border-[#6589ff] bg-[#0d0d0d]"
                  : "border-[#2b2b2b] bg-[#0d0d0d] hover:border-[#3a3a3a] hover:bg-[#111]"
              }`}
              style={active ? { boxShadow: "0 0 0 2px rgba(101,137,255,0.25)" } : undefined}
            >
              {/* Preview swatch — abstract accent visual */}
              <div className="h-24 rounded-xl mb-4 relative overflow-hidden border border-[#2b2b2b] bg-[#111]">
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(101,137,255,0.20) 0%, rgba(101,137,255,0.02) 50%, rgba(255,255,255,0.05) 100%)",
                  }}
                />
                <div className="absolute inset-3 flex items-end">
                  <div className="text-[10px] uppercase tracking-widest text-[#7a7a7a]">
                    Preview
                  </div>
                </div>
                <div className="absolute right-3 top-3 text-[11px] font-semibold text-white/80">
                  {s.name}
                </div>
              </div>
              <div className="font-semibold text-[15px] tracking-tight">{s.name}</div>
              <div className="text-xs text-[#a0a0a0] mt-1 leading-relaxed">{s.desc}</div>
            </button>
          );
        })}

        {/* Custom style */}
        <button
          type="button"
          onClick={() => onChange({ style_id: "custom" })}
          data-testid={`style-card-${svc.id}-custom`}
          className={`text-left squircle border p-5 btn-anim ${
            entry.style_id === "custom"
              ? "border-[#6589ff] bg-[#0d0d0d]"
              : "border-[#2b2b2b] bg-[#0d0d0d] hover:border-[#3a3a3a] hover:bg-[#111]"
          }`}
          style={
            entry.style_id === "custom"
              ? { boxShadow: "0 0 0 2px rgba(101,137,255,0.25)" }
              : undefined
          }
        >
          <div className="h-24 rounded-xl mb-4 border border-dashed border-[#2b2b2b] flex items-center justify-center text-[#666] text-xs">
            Describe your own
          </div>
          <div className="font-semibold text-[15px] tracking-tight">Custom style</div>
          <div className="text-xs text-[#a0a0a0] mt-1 leading-relaxed">
            Reference links or description — we'll match it.
          </div>
        </button>
      </div>

      {entry.style_id === "custom" && (
        <div className="mt-5">
          <label className="text-xs uppercase tracking-widest text-[#a0a0a0]">
            Describe your style
          </label>
          <textarea
            data-testid={`custom-style-${svc.id}`}
            value={entry.custom_style || ""}
            onChange={(e) => onChange({ custom_style: e.target.value })}
            rows={4}
            placeholder="Reference links, mood-boards, creators you admire…"
            className="mt-2 w-full rounded-2xl bg-[#0d0d0d] border border-[#2b2b2b] p-4 text-sm text-white placeholder:text-[#666] focus:outline-none focus:border-[#4a4a4a]"
          />
        </div>
      )}
    </div>
  );
}

/* ================================================================== */
/* Step 2B: Quantity                                                  */
/* ================================================================== */
function Step2Quantity({ svc, entry, onChange }) {
  const presets = svc.recommended_quantities || [1];
  return (
    <div data-testid="step-2-qty">
      <p className="text-[#a0a0a0] max-w-lg mb-6">
        How many <span className="text-white">{svc.unit_label_plural || svc.unit_label + "s"}</span>{" "}
        do you want in this order?
      </p>

      {/* Recommendation */}
      <div
        className="squircle border border-[#2b2b2b] bg-[#0d0d0d] p-5 mb-6 flex gap-4"
        data-testid={`qty-note-${svc.id}`}
      >
        <Info size={16} className="text-[#6589ff] shrink-0 mt-0.5" />
        <div className="text-sm text-[#a0a0a0] leading-relaxed">{svc.quantity_note}</div>
      </div>

      {/* Viral note for volume short */}
      {svc.viral_note && (
        <div
          className="squircle border border-[#2b2b2b] bg-[#0d0d0d] p-5 mb-6"
          data-testid={`viral-note-${svc.id}`}
        >
          <div className="text-[11px] uppercase tracking-widest text-[#6589ff] mb-2">
            Important
          </div>
          <div className="text-sm text-[#a0a0a0] leading-relaxed">{svc.viral_note}</div>
        </div>
      )}

      {/* Presets */}
      <div className="flex flex-wrap gap-2 mb-4">
        {presets.map((n) => {
          const active = entry.quantity === n;
          return (
            <button
              key={n}
              type="button"
              onClick={() => onChange({ quantity: n })}
              data-testid={`qty-preset-${svc.id}-${n}`}
              className={`squircle-sm px-5 h-11 text-sm border btn-anim ${
                active
                  ? "border-[#6589ff] text-white"
                  : "border-[#2b2b2b] bg-[#0d0d0d] text-[#a0a0a0] hover:border-[#3a3a3a] hover:text-white"
              }`}
              style={active ? { boxShadow: "0 0 0 2px rgba(101,137,255,0.25)" } : undefined}
            >
              {n} {svc.unit_label_plural || svc.unit_label + "s"}
            </button>
          );
        })}
      </div>

      {/* Free-form quantity */}
      <label className="block text-xs uppercase tracking-widest text-[#a0a0a0] mb-2">
        Or enter a custom amount
      </label>
      <input
        type="number"
        min={1}
        data-testid={`qty-input-${svc.id}`}
        value={entry.quantity || ""}
        onChange={(e) => onChange({ quantity: parseInt(e.target.value || "0", 10) })}
        placeholder="e.g. 24"
        className="w-40 rounded-full bg-[#0d0d0d] border border-[#2b2b2b] px-5 h-11 text-sm text-white placeholder:text-[#666] focus:outline-none focus:border-[#4a4a4a]"
      />

      {/* Long-form footage length */}
      {svc.ask_footage_length && (
        <div className="mt-8">
          <label className="block text-xs uppercase tracking-widest text-[#a0a0a0] mb-2">
            Approx. length of raw footage (minutes)
          </label>
          <p className="text-xs text-[#7a7a7a] mb-3">
            Longer footage may extend delivery time and adjust the final price.
          </p>
          <input
            type="number"
            min={1}
            data-testid={`footage-length-${svc.id}`}
            value={entry.footage_minutes || ""}
            onChange={(e) =>
              onChange({ footage_minutes: parseInt(e.target.value || "0", 10) })
            }
            placeholder="e.g. 45"
            className="w-40 rounded-full bg-[#0d0d0d] border border-[#2b2b2b] px-5 h-11 text-sm text-white placeholder:text-[#666] focus:outline-none focus:border-[#4a4a4a]"
          />
        </div>
      )}
    </div>
  );
}

/* ================================================================== */
/* Step 3: Raw content                                                */
/* ================================================================== */
function Step3Content({ svc, entry, onChange }) {
  const links = entry.source_links?.length ? entry.source_links : [""];
  const setLinks = (arr) => onChange({ source_links: arr });

  return (
    <div data-testid="step-3">
      <p className="text-[#a0a0a0] max-w-xl mb-6">
        For <span className="text-white">{svc.name}</span>: {svc.input_hint}
      </p>

      {svc.requires_raw_content && (
        <div className="space-y-3">
          {links.map((l, i) => (
            <div key={i} className="flex gap-2">
              <input
                data-testid={`link-input-${svc.id}-${i}`}
                value={l}
                onChange={(e) => {
                  const nx = [...links];
                  nx[i] = e.target.value;
                  setLinks(nx);
                }}
                placeholder="https://…"
                className="flex-1 rounded-full bg-[#0d0d0d] border border-[#2b2b2b] px-5 h-11 text-sm text-white placeholder:text-[#666] focus:outline-none focus:border-[#4a4a4a]"
              />
              {links.length > 1 && (
                <button
                  type="button"
                  aria-label="Remove link"
                  onClick={() => setLinks(links.filter((_, x) => x !== i))}
                  className="h-11 w-11 rounded-full border border-[#2b2b2b] flex items-center justify-center hover:bg-[#111] btn-anim"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={() => setLinks([...links, ""])}
            data-testid={`add-link-${svc.id}`}
            className="squircle-sm border border-[#2b2b2b] px-4 h-10 text-sm text-[#a0a0a0] hover:bg-[#111] hover:text-white btn-anim inline-flex items-center gap-2"
          >
            <Plus size={14} /> Add another link
          </button>
        </div>
      )}

      {svc.allow_context_text && (
        <div className="mt-6">
          <label className="text-xs uppercase tracking-widest text-[#a0a0a0]">
            {svc.requires_raw_content ? "Notes / context (optional)" : "Details for your team"}
          </label>
          <textarea
            data-testid={`context-${svc.id}`}
            value={entry.source_context || ""}
            onChange={(e) => onChange({ source_context: e.target.value })}
            rows={5}
            placeholder="Anything the editor should know — brand tone, examples, must-avoid, references."
            className="mt-2 w-full rounded-2xl bg-[#0d0d0d] border border-[#2b2b2b] p-4 text-sm text-white placeholder:text-[#666] focus:outline-none focus:border-[#4a4a4a]"
          />
        </div>
      )}

      <div className="mt-6">
        <label className="text-xs uppercase tracking-widest text-[#a0a0a0]">
          Extra notes for this service (optional)
        </label>
        <textarea
          data-testid={`notes-${svc.id}`}
          value={entry.notes || ""}
          onChange={(e) => onChange({ notes: e.target.value })}
          rows={3}
          placeholder="Anything else we should know for this specific piece?"
          className="mt-2 w-full rounded-2xl bg-[#0d0d0d] border border-[#2b2b2b] p-4 text-sm text-white placeholder:text-[#666] focus:outline-none focus:border-[#4a4a4a]"
        />
      </div>
    </div>
  );
}

/* ================================================================== */
/* Billing selector — 2-tier                                          */
/* ================================================================== */
function BillingSelector({ subscription, setSubscription }) {
  const isSub = subscription !== "one_time";
  // If they pick Subscription, default to monthly if not already a sub value
  const setMode = (mode) => {
    if (mode === "one_time") setSubscription("one_time");
    else if (!isSub) setSubscription("monthly");
  };

  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-[#a0a0a0] mb-3">
        Billing
      </div>

      {/* Primary toggle */}
      <div
        role="tablist"
        aria-label="Billing mode"
        className="relative inline-flex bg-[#0d0d0d] border border-[#2b2b2b] rounded-full p-1"
        data-testid="billing-mode-toggle"
      >
        {[
          { id: "one_time", label: "One-Time Purchase" },
          { id: "subscription", label: "Subscription" },
        ].map((opt) => {
          const active =
            (opt.id === "one_time" && !isSub) ||
            (opt.id === "subscription" && isSub);
          return (
            <button
              key={opt.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setMode(opt.id)}
              data-testid={`billing-mode-${opt.id}`}
              className="relative z-10 px-5 sm:px-6 h-10 text-sm font-medium btn-anim rounded-full"
              style={{ color: active ? "#ffffff" : "#8f8f96" }}
            >
              {active && (
                <motion.span
                  layoutId="billing-mode-pill"
                  aria-hidden
                  className="absolute inset-0 rounded-full"
                  style={{
                    background: "#6589ff",
                    boxShadow:
                      "0 8px 20px -8px rgba(101,137,255,0.7), inset 0 1px 0 rgba(255,255,255,0.22)",
                  }}
                  transition={{ type: "spring", stiffness: 380, damping: 30 }}
                />
              )}
              <span className="relative z-10">{opt.label}</span>
            </button>
          );
        })}
      </div>

      {/* Interval selector — collapses in/out */}
      <AnimatePresence initial={false}>
        {isSub && (
          <motion.div
            key="interval"
            initial={{ opacity: 0, height: 0, y: -6 }}
            animate={{ opacity: 1, height: "auto", y: 0 }}
            exit={{ opacity: 0, height: 0, y: -6 }}
            transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
            data-testid="billing-interval-panel"
          >
            <div className="pt-5 flex flex-wrap items-center gap-3">
              <div className="text-[11px] uppercase tracking-widest text-[#a0a0a0]">
                Interval
              </div>
              <div
                role="tablist"
                aria-label="Billing interval"
                className="relative inline-flex bg-[#0d0d0d] border border-[#2b2b2b] rounded-full p-1"
              >
                {[
                  { id: "monthly", label: "Monthly", note: "−5%" },
                  { id: "yearly", label: "Annual", note: "−25%" },
                ].map((opt) => {
                  const active = subscription === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      onClick={() => setSubscription(opt.id)}
                      data-testid={`billing-interval-${opt.id}`}
                      className="relative z-10 px-4 sm:px-5 h-9 text-sm rounded-full btn-anim inline-flex items-center gap-2"
                      style={{ color: active ? "#ffffff" : "#8f8f96" }}
                    >
                      {active && (
                        <motion.span
                          layoutId="billing-interval-pill"
                          aria-hidden
                          className="absolute inset-0 rounded-full"
                          style={{
                            background: "rgba(101,137,255,0.18)",
                            border: "1px solid #6589ff",
                            boxShadow: "0 0 0 3px rgba(101,137,255,0.10)",
                          }}
                          transition={{ type: "spring", stiffness: 380, damping: 30 }}
                        />
                      )}
                      <span className="relative z-10">{opt.label}</span>
                      <span
                        className="relative z-10 text-[10px] font-semibold tracking-wider"
                        style={{ color: active ? "#8aa4ff" : "#6a6a70" }}
                      >
                        {opt.note}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ================================================================== */
/* Step 4: Review & price                                             */
/* ================================================================== */
function Step4Review({ services, selected, data, subscription, setSubscription, price, primeMe }) {
  const isPrime = primeMe?.status === "active";
  const primePro = primeMe?.plan === "prime_pro";
  // What a non-member could save by joining Prime (monthly cost included)
  const nonMemberPreview = !isPrime && price.subtotal > 0
    ? {
        prime: {
          save: price.subtotal * 0.15,
          net: (price.subtotal * 0.15) - 49,
        },
        primePro: {
          save: price.subtotal * 0.25,
          net: (price.subtotal * 0.25) - 119,
        },
      }
    : null;
  return (
    <div data-testid="step-4">
      <p className="text-[#a0a0a0] mb-8 max-w-xl">
        Here's the full picture. Review your order and we'll get your editors moving.
      </p>

      {/* Prime banner — either an ACTIVE member confirmation, or an upsell */}
      {isPrime ? (
        <div
          className="mb-8 squircle border p-5 flex items-center justify-between gap-4 flex-wrap"
          style={{
            borderColor: "rgba(168,85,247,0.5)",
            background: "linear-gradient(120deg, rgba(168,85,247,0.14) 0%, rgba(168,85,247,0.02) 100%)",
          }}
          data-testid="prime-active-banner"
        >
          <div>
            <div className="text-[11px] uppercase tracking-widest" style={{ color: "#a855f7" }}>
              {primePro ? "Pro Plus member" : "Pro member"}
            </div>
            <div className="text-lg font-semibold mt-1">
              You're saving ${Math.round(price.discount)} on this order
            </div>
            <div className="text-xs text-[#a0a0a0] mt-0.5">
              {primeMe.discount_percentage}% member discount · faster ETA · priority queue
            </div>
          </div>
          <div
            className="squircle-sm px-3 h-8 inline-flex items-center gap-1.5 text-xs font-semibold"
            style={{ background: "rgba(168,85,247,0.18)", border: "1px solid rgba(168,85,247,0.5)", color: "#a855f7" }}
          >
            <Rocket size={12} /> Priority
          </div>
        </div>
      ) : nonMemberPreview && (
        <Link
          to="/prime"
          className="mb-8 squircle border p-5 flex items-center justify-between gap-4 flex-wrap block btn-anim"
          style={{
            borderColor: "rgba(168,85,247,0.32)",
            background: "linear-gradient(180deg, rgba(168,85,247,0.09) 0%, rgba(168,85,247,0.02) 100%)",
          }}
          data-testid="prime-upsell-banner"
        >
          <div>
            <div className="text-[11px] uppercase tracking-widest" style={{ color: "#a855f7" }}>
              Join Bliencer Pro
            </div>
            <div className="text-base font-semibold mt-1">
              Save ${Math.round(nonMemberPreview.prime.save)} on this order with Pro
              {nonMemberPreview.primePro.save > nonMemberPreview.prime.save && (
                <span className="text-[#a0a0a0] font-normal">
                  {" "}· or ${Math.round(nonMemberPreview.primePro.save)} with Pro Plus
                </span>
              )}
            </div>
            <div className="text-xs text-[#a0a0a0] mt-0.5">
              $49/mo · 15% off every order · priority queue · unlimited revisions
            </div>
          </div>
          <span className="text-xs font-medium inline-flex items-center gap-1.5" style={{ color: "#a855f7" }}>
            Explore Prime <ArrowRight size={12} />
          </span>
        </Link>
      )}

      {/* Line items */}
      <div className="space-y-3">
        {selected.map((sid) => {
          const svc = services.find((s) => s.id === sid);
          const e = data[sid] || {};
          const line = price.rows.find((r) => r.sid === sid);
          const style = svc?.styles.find((s) => s.id === e.style_id);
          return (
            <div
              key={sid}
              className="squircle border border-[#2b2b2b] bg-[#0d0d0d] p-5 flex items-start gap-4"
              data-testid={`review-line-${sid}`}
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold">{svc?.name}</div>
                <div className="text-xs text-[#a0a0a0] mt-1">
                  {e.quantity} {svc?.unit_label_plural || svc?.unit_label + "s"} ·{" "}
                  {style?.name || (e.style_id === "custom" ? "Custom style" : "Style")}
                  {svc?.ask_footage_length && e.footage_minutes
                    ? ` · ~${e.footage_minutes} min raw`
                    : ""}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-lg font-semibold tracking-tight">
                  ${Math.round(line?.line || 0)}
                </div>
                <div className="text-[11px] text-[#7a7a7a]">line total</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Totals — the ONLY place the price appears */}
      <div
        className="mt-6 squircle border p-6"
        style={{
          borderColor: "#6589ff",
          background: "linear-gradient(180deg, rgba(101,137,255,0.06) 0%, transparent 100%)",
        }}
        data-testid="price-summary"
      >
        <div className="flex items-center justify-between text-sm">
          <span className="text-[#a0a0a0]">Subtotal</span>
          <span>${Math.round(price.subtotal)}</span>
        </div>
        {price.discountRate > 0 && (
          <div className="flex items-center justify-between text-sm mt-2">
            <span className="text-[#a0a0a0]">
              {isPrime
                ? `${primePro ? "Pro Plus" : "Pro"} member discount (${Math.round(price.discountRate * 100)}%)`
                : `Subscription discount (${Math.round(price.discountRate * 100)}%)`}
            </span>
            <span style={isPrime ? { color: "#a855f7" } : undefined}>−${Math.round(price.discount)}</span>
          </div>
        )}
        <div className="mt-4 border-t border-[#2b2b2b] pt-4 flex items-center justify-between">
          <span className="text-sm text-[#a0a0a0]">Total</span>
          <span className="text-3xl font-bold tracking-tighter" data-testid="price-total">
            ${Math.round(price.total)}
          </span>
        </div>
        <div className="mt-2 text-[11px] text-[#7a7a7a] flex items-center gap-1.5">
          <Clock size={11} /> Delivery estimated within {isPrime ? (primePro ? "2–5" : "3–6") : "4–7"} business days
        </div>
      </div>
    </div>
  );
}
